import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, BarChart3, Download, Search, ChevronLeft, ChevronRight, ChevronDown,
  Shield, Edit3, X, Check, Clock, Crown, Trash2, AlertTriangle, GraduationCap, CreditCard, Package, Scale, FileText, UserX
} from 'lucide-react';

import API from '../utils/api';
import { downloadUserDataPdf } from '../utils/userDataPdf';

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="card-surface p-3 sm:p-4 flex items-center gap-3 sm:gap-4" data-testid={'stat-' + label.toLowerCase().replace(/\s/g, '-')}>
      <div className={'w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ' + color}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xl sm:text-2xl font-bold">{value}</p>
        <p className="text-xs sm:text-xs text-white/50 truncate">{label}</p>
      </div>
    </div>
  );
}


function TosAcceptanceLogs() {
  var [open, setOpen] = useState(false);
  var [logs, setLogs] = useState([]);
  var [total, setTotal] = useState(0);
  var [loading, setLoading] = useState(false);
  var [exporting, setExporting] = useState(false);

  function load() {
    setLoading(true);
    axios.get(API + '/tos/admin/acceptances?per_page=50', { withCredentials: true })
      .then(function (r) { setLogs(r.data.logs || []); setTotal(r.data.total || 0); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }

  function exportCsv() {
    setExporting(true);
    axios.get(API + '/tos/admin/acceptances/export', { withCredentials: true, responseType: 'blob' })
      .then(function (r) {
        var url = URL.createObjectURL(new Blob([r.data]));
        var a = document.createElement('a'); a.href = url; a.download = 'tos_acceptances.csv'; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(function () { alert('Export failed'); })
      .finally(function () { setExporting(false); });
  }

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="tos-acceptance-section">
      <button onClick={function () { if (!open) load(); setOpen(!open); }}
        className="w-full flex items-center justify-between"
        data-testid="tos-acceptance-toggle">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-r from-[#5A4E9E] to-[#2D2560] flex items-center justify-center"><Scale className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div>
          <span className="text-sm font-semibold">ToS Acceptance Logs</span>
        </div>
        <ChevronDown className={'w-4 h-4 text-white/30 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-white/40">{total} total acceptance records</p>
            <button onClick={exportCsv} disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 hover:bg-white/10 disabled:opacity-40"
              data-testid="tos-export-csv-btn">
              <Download className="w-3.5 h-3.5" /> {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
          {loading ? (
            <div className="py-4 text-center text-white/30 text-xs">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="py-4 text-center text-white/30 text-xs">No acceptance records yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] text-xs" data-testid="tos-acceptance-table">
                <thead>
                  <tr className="border-b border-white/10 text-white/40">
                    <th className="text-left py-2 px-2">User</th>
                    <th className="text-left py-2 px-2">ToS Ver</th>
                    <th className="text-left py-2 px-2">Accepted At</th>
                    <th className="text-left py-2 px-2">IP</th>
                    <th className="text-left py-2 px-2">Context</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(function (log, i) {
                    var at = log.accepted_at ? new Date(log.accepted_at).toLocaleString() : '';
                    return (
                      <tr key={i} className="border-b border-white/[0.04] text-white/60">
                        <td className="py-2 px-2 truncate max-w-[180px]">{log.email || log.user_id || '-'}</td>
                        <td className="py-2 px-2">{log.tos_version || '-'}</td>
                        <td className="py-2 px-2">{at}</td>
                        <td className="py-2 px-2 font-mono text-xs">{log.ip_address || '-'}</td>
                        <td className="py-2 px-2">{log.context || 'login'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LegalRequestsPanel() {
  var [open, setOpen] = useState(false);
  var [requests, setRequests] = useState([]);
  var [total, setTotal] = useState(0);
  var [loading, setLoading] = useState(false);
  var [showForm, setShowForm] = useState(false);
  var [form, setForm] = useState({ request_type: 'subpoena', jurisdiction: '', target_user_email: '', description: '', notes: '' });
  var [saving, setSaving] = useState(false);
  var [pendingDeletions, setPendingDeletions] = useState([]);

  function load() {
    setLoading(true);
    Promise.all([
      axios.get(API + '/legal/requests?per_page=50', { withCredentials: true }),
      axios.get(API + '/legal/admin/pending-deletions', { withCredentials: true }),
    ]).then(function (res) {
      setRequests(res[0].data.requests || []);
      setTotal(res[0].data.total || 0);
      setPendingDeletions(res[1].data.pending || []);
    }).catch(function () {}).finally(function () { setLoading(false); });
  }

  function createRequest() {
    setSaving(true);
    axios.post(API + '/legal/requests', form, { withCredentials: true })
      .then(function () { setShowForm(false); setForm({ request_type: 'subpoena', jurisdiction: '', target_user_email: '', description: '', notes: '' }); load(); })
      .catch(function (e) { alert(e.response?.data?.detail || 'Failed'); })
      .finally(function () { setSaving(false); });
  }

  function updateStatus(reqId, newStatus) {
    axios.patch(API + '/legal/requests/' + reqId, { status: newStatus }, { withCredentials: true })
      .then(function () { load(); })
      .catch(function () {});
  }

  function notifyUser(reqId) {
    if (!window.confirm('Notify the target user about this legal request?')) return;
    axios.post(API + '/legal/requests/' + reqId + '/notify-user', {}, { withCredentials: true })
      .then(function () { alert('User notified'); load(); })
      .catch(function (e) { alert(e.response?.data?.detail || 'Failed'); });
  }

  function exportCsv() {
    axios.get(API + '/legal/requests/export', { withCredentials: true, responseType: 'blob' })
      .then(function (r) { var url = URL.createObjectURL(new Blob([r.data])); var a = document.createElement('a'); a.href = url; a.download = 'legal_requests.csv'; a.click(); })
      .catch(function () { alert('Export failed'); });
  }

  function runHardDelete() {
    if (!window.confirm('Run hard delete for all accounts past their 30-day grace period? This is irreversible.')) return;
    axios.post(API + '/legal/admin/hard-delete-expired', {}, { withCredentials: true })
      .then(function (r) { alert('Deleted ' + r.data.deleted_count + ' accounts'); load(); })
      .catch(function (e) { alert(e.response?.data?.detail || 'Failed'); });
  }

  var statusColors = { pending: '#EAB308', in_progress: '#3B82F6', complied: '#10B981', contested: '#EF4444', resolved: '#71717A' };

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="legal-requests-section">
      <button onClick={function () { if (!open) load(); setOpen(!open); }}
        className="w-full flex items-center justify-between" data-testid="legal-requests-toggle">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-r from-[#EF4444] to-[#F97316] flex items-center justify-center"><Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div>
          <span className="text-sm font-semibold">Legal Requests & Data Retention</span>
        </div>
        <ChevronDown className={'w-4 h-4 text-white/30 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          {/* Pending Deletions */}
          {pendingDeletions.length > 0 && (
            <div className="p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl space-y-2" data-testid="pending-deletions">
              <p className="text-xs font-semibold text-[#F59E0B]">Pending Account Deletions ({pendingDeletions.length})</p>
              {pendingDeletions.map(function (d, i) {
                return (
                  <div key={i} className="flex items-center justify-between text-xs text-white/50 py-1 border-b border-white/[0.04] last:border-0">
                    <span>{d.email || d.name}</span>
                    <span className="text-white/30">{d.deletion_scheduled_at ? new Date(d.deletion_scheduled_at).toLocaleDateString() : '-'}</span>
                  </div>
                );
              })}
              <button onClick={runHardDelete} className="text-xs text-[#EF4444] hover:underline mt-1" data-testid="run-hard-delete-btn">
                Run Hard Delete for Expired
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-white/40">{total} legal request(s)</p>
            <div className="flex gap-2">
              <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 hover:bg-white/10" data-testid="legal-export-btn">
                <Download className="w-3.5 h-3.5" /> Export
              </button>
              <button onClick={function () { setShowForm(!showForm); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#5A4E9E]/20 border border-[#5A4E9E]/30 rounded-lg text-xs text-[#5A4E9E] hover:bg-[#5A4E9E]/30" data-testid="new-legal-request-btn">
                + New Request
              </button>
            </div>
          </div>

          {/* Create Form */}
          {showForm && (
            <div className="p-4 bg-white/[0.02] border border-white/[0.08] rounded-xl space-y-3" data-testid="legal-request-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/40 mb-1 block">Type</label>
                  <select value={form.request_type} onChange={function (e) { setForm(Object.assign({}, form, { request_type: e.target.value })); }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none">
                    <option value="subpoena">Subpoena</option>
                    <option value="court_order">Court Order</option>
                    <option value="law_enforcement">Law Enforcement</option>
                    <option value="regulatory">Regulatory</option>
                    <option value="business_record_affidavit">Business Record Affidavit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/40 mb-1 block">Jurisdiction</label>
                  <input value={form.jurisdiction} onChange={function (e) { setForm(Object.assign({}, form, { jurisdiction: e.target.value })); }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none" placeholder="e.g., Maryland Circuit Court" />
                </div>
              </div>
              <div>
                <label className="text-xs text-white/40 mb-1 block">Target User Email</label>
                <input value={form.target_user_email} onChange={function (e) { setForm(Object.assign({}, form, { target_user_email: e.target.value })); }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none" placeholder="user@example.com" />
              </div>
              <div>
                <label className="text-xs text-white/40 mb-1 block">Description</label>
                <textarea value={form.description} onChange={function (e) { setForm(Object.assign({}, form, { description: e.target.value })); }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none resize-none h-16" placeholder="Brief description of request" />
              </div>
              <div className="flex gap-2">
                <button onClick={function () { setShowForm(false); }} className="flex-1 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60">Cancel</button>
                <button onClick={createRequest} disabled={saving} className="flex-1 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg text-xs font-medium disabled:opacity-40" data-testid="submit-legal-request-btn">
                  {saving ? 'Saving...' : 'Log Request'}
                </button>
              </div>
            </div>
          )}

          {/* Requests Table */}
          {loading ? (
            <div className="py-4 text-center text-white/30 text-xs">Loading...</div>
          ) : requests.length === 0 ? (
            <div className="py-4 text-center text-white/30 text-xs">No legal requests logged</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-xs" data-testid="legal-requests-table">
                <thead>
                  <tr className="border-b border-white/10 text-white/40">
                    <th className="text-left py-2 px-2">ID</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-left py-2 px-2">Target</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(function (r) {
                    var sc = statusColors[r.status] || '#71717A';
                    var isBra = r.request_type === 'business_record_affidavit';
                    var braName = isBra ? [r.bra_first_name, r.bra_last_name].filter(Boolean).join(' ') : '';
                    var braDelivery = isBra && r.bra_delivery_method ? r.bra_delivery_method.replace(/_/g, ' ') : '';
                    return (
                      <tr key={r.request_id} className="border-b border-white/[0.04] text-white/60" title={r.description || ''}>
                        <td className="py-2 px-2 font-mono text-xs">{r.request_id}</td>
                        <td className="py-2 px-2 capitalize">
                          {(r.request_type || '').replace(/_/g, ' ')}
                          {isBra && (
                            <div className="text-[10px] text-white/35 mt-0.5 normal-case">
                              {braDelivery}{r.bra_amount_paid ? ' · paid $' + r.bra_amount_paid : ''}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-2 truncate max-w-[180px]">
                          <div>{r.target_user_email || '-'}</div>
                          {isBra && braName && <div className="text-[10px] text-white/40 truncate">{braName}</div>}
                        </td>
                        <td className="py-2 px-2">
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase" style={{ background: sc + '15', color: sc }}>{r.status}</span>
                        </td>
                        <td className="py-2 px-2 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                        <td className="py-2 px-2">
                          <div className="flex gap-1">
                            {r.status === 'pending' && <button onClick={function () { updateStatus(r.request_id, 'in_progress'); }} className="px-2 py-0.5 bg-[#3B82F6]/15 text-[#3B82F6] rounded text-xs">In Progress</button>}
                            {r.status !== 'resolved' && <button onClick={function () { updateStatus(r.request_id, 'resolved'); }} className="px-2 py-0.5 bg-[#10B981]/15 text-[#10B981] rounded text-xs">Resolve</button>}
                            {!r.user_notified && r.target_user_email && <button onClick={function () { notifyUser(r.request_id); }} className="px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] rounded text-xs">Notify</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SafetyModerationPanel() {
  var [open, setOpen] = useState(false);
  var [incidents, setIncidents] = useState([]);
  var [suspensions, setSuspensions] = useState([]);
  var [loading, setLoading] = useState(false);
  var [suspendForm, setSuspendForm] = useState({ email: '', reason: '', duration_minutes: 30 });
  var [showSuspendForm, setShowSuspendForm] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([
      axios.get(API + '/admin/safety-incidents', { withCredentials: true }),
      axios.get(API + '/admin/messaging-suspensions', { withCredentials: true }),
    ]).then(function (res) {
      setIncidents(res[0].data || []);
      setSuspensions(res[1].data || []);
    }).catch(function () {}).finally(function () { setLoading(false); });
  }

  function resolveIncident(idx, status, notes) {
    axios.patch(API + '/admin/safety-incidents/' + idx + '/resolve', { status: status, notes: notes || '', resolution: status }, { withCredentials: true })
      .then(function () { load(); });
  }

  function manualSuspend() {
    axios.post(API + '/admin/messaging-suspensions/manual', suspendForm, { withCredentials: true })
      .then(function () { setShowSuspendForm(false); load(); alert('User suspended'); })
      .catch(function (e) { alert(e.response?.data?.detail || 'Failed'); });
  }

  function restoreUser(userId) {
    axios.post(API + '/admin/messaging-suspensions/' + userId + '/restore', {}, { withCredentials: true })
      .then(function () { load(); alert('Messaging restored'); })
      .catch(function (e) { alert(e.response?.data?.detail || 'Failed'); });
  }

  function exportCsv() {
    axios.get(API + '/admin/safety-incidents/export', { withCredentials: true, responseType: 'blob' })
      .then(function (r) { var url = URL.createObjectURL(new Blob([r.data])); var a = document.createElement('a'); a.href = url; a.download = 'safety_incidents.csv'; a.click(); })
      .catch(function () { alert('Export failed'); });
  }

  var incidentColors = { pending: '#EAB308', reviewed: '#3B82F6', resolved: '#10B981', escalated: '#EF4444' };

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="safety-moderation-section">
      <button onClick={function () { if (!open) load(); setOpen(!open); }}
        className="w-full flex items-center justify-between" data-testid="safety-moderation-toggle">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-r from-[#EF4444] to-[#2D2560] flex items-center justify-center"><AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div>
          <span className="text-sm font-semibold">Safety Moderation</span>
        </div>
        <ChevronDown className={'w-4 h-4 text-white/30 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          {/* Actions */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-white/40">{incidents.length} incident(s) | {suspensions.length} suspension(s)</p>
            <div className="flex gap-2">
              <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 hover:bg-white/10" data-testid="safety-export-btn">
                <Download className="w-3.5 h-3.5" /> Export
              </button>
              <button onClick={function () { setShowSuspendForm(!showSuspendForm); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EF4444]/15 border border-[#EF4444]/25 rounded-lg text-xs text-[#EF4444] hover:bg-[#EF4444]/25" data-testid="manual-suspend-btn">
                Suspend User
              </button>
            </div>
          </div>

          {/* Manual Suspend Form */}
          {showSuspendForm && (
            <div className="p-3 bg-white/[0.02] border border-white/[0.08] rounded-xl space-y-2" data-testid="suspend-form">
              <input value={suspendForm.email} onChange={function (e) { setSuspendForm(Object.assign({}, suspendForm, { email: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white" placeholder="User email" />
              <input value={suspendForm.reason} onChange={function (e) { setSuspendForm(Object.assign({}, suspendForm, { reason: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white" placeholder="Reason" />
              <div className="flex gap-2">
                <button onClick={manualSuspend} className="flex-1 py-2 bg-[#EF4444] text-white rounded-lg text-xs font-medium" data-testid="confirm-suspend-btn">Suspend</button>
                <button onClick={function () { setShowSuspendForm(false); }} className="flex-1 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/50">Cancel</button>
              </div>
            </div>
          )}

          {loading ? <div className="py-4 text-center text-white/30 text-xs">Loading...</div> : (
            <>
              {/* Incidents */}
              {incidents.length > 0 && (
                <div>
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-2 font-bold">Blocked Message Incidents</p>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {incidents.map(function (inc, i) {
                      var sc = incidentColors[inc.moderation_status] || '#71717A';
                      return (
                        <div key={i} className="p-3 bg-white/[0.02] border border-white/[0.05] rounded-xl text-xs space-y-1.5" data-testid={'incident-' + i}>
                          <div className="flex items-center justify-between">
                            <span className="text-white/60 font-medium">{inc.sender_name || inc.sender_id}</span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: sc + '15', color: sc }}>{inc.moderation_status}</span>
                          </div>
                          <p className="text-white/40">{inc.violation_reason}</p>
                          <div className="flex items-center gap-3 text-xs text-white/25">
                            <span>{inc.classification}</span>
                            {inc.self_harm_detected && <span className="text-[#EF4444]">self-harm</span>}
                            {inc.violence_detected && <span className="text-[#F97316]">violence</span>}
                            {inc.child_safety_detected && <span className="text-[#EAB308]">child-safety</span>}
                            <span>{inc.created_at ? new Date(inc.created_at).toLocaleString() : ''}</span>
                          </div>
                          {inc.moderation_status === 'pending' && (
                            <div className="flex gap-1.5 pt-1">
                              <button onClick={function () { resolveIncident(i, 'reviewed', ''); }} className="px-2 py-0.5 bg-[#3B82F6]/15 text-[#3B82F6] rounded text-xs">Mark Reviewed</button>
                              <button onClick={function () { resolveIncident(i, 'resolved', ''); }} className="px-2 py-0.5 bg-[#10B981]/15 text-[#10B981] rounded text-xs">Resolve</button>
                              <button onClick={function () { resolveIncident(i, 'escalated', ''); }} className="px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded text-xs">Escalate</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Suspensions */}
              {suspensions.length > 0 && (
                <div>
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-2 font-bold">Messaging Suspensions</p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {suspensions.map(function (s, i) {
                      var isActive = s.active && new Date(s.end_time) > new Date();
                      return (
                        <div key={i} className="p-3 bg-white/[0.02] border border-white/[0.05] rounded-xl text-xs flex items-center justify-between" data-testid={'suspension-' + i}>
                          <div>
                            <span className="text-white/60 font-medium">{s.user_name || s.user_id}</span>
                            <span className={'ml-2 px-1.5 py-0.5 rounded text-xs font-bold ' + (isActive ? 'bg-[#EF4444]/15 text-[#EF4444]' : 'bg-white/5 text-white/25')}>
                              {isActive ? 'ACTIVE' : 'EXPIRED'}
                            </span>
                            <p className="text-white/30 mt-0.5">{s.reason} | Repeat: {s.repeat_count || 0}</p>
                          </div>
                          {isActive && (
                            <button onClick={function () { restoreUser(s.user_id); }}
                              className="px-2 py-1 bg-[#10B981]/15 text-[#10B981] rounded text-xs" data-testid={'restore-' + i}>
                              Restore
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {incidents.length === 0 && suspensions.length === 0 && (
                <div className="py-4 text-center text-white/30 text-xs">No safety incidents or suspensions recorded</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminPanel() {
  var [stats, setStats] = useState(null);
  var [users, setUsers] = useState([]);
  var [total, setTotal] = useState(0);
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [search, setSearch] = useState('');
  var [roleFilter, setRoleFilter] = useState('');
  var [planFilter, setPlanFilter] = useState('');
  var [editUser, setEditUser] = useState(null);
  var [editForm, setEditForm] = useState({});
  var [saving, setSaving] = useState(false);
  var [exporting, setExporting] = useState(false);
  var [auditLogs, setAuditLogs] = useState([]);
  var [deleteTarget, setDeleteTarget] = useState(null);
  var [deleting, setDeleting] = useState(false);
  var [stripeWarning, setStripeWarning] = useState('');
  var [coursePurchasers, setCoursePurchasers] = useState([]);
  var [cpOpen, setCpOpen] = useState(false);
  var [cpLoading, setCpLoading] = useState(false);
  var [cpExpanded, setCpExpanded] = useState(null);
  var [deletedUsers, setDeletedUsers] = useState([]);
  var [deletedOpen, setDeletedOpen] = useState(false);
  var [deletedLoading, setDeletedLoading] = useState(false);
  var [downloadingUid, setDownloadingUid] = useState(null);
  var [sweeping, setSweeping] = useState(false);
  var [grantedCourses, setGrantedCourses] = useState([]);
  var [grantSeries, setGrantSeries] = useState('');
  var [grantBusy, setGrantBusy] = useState(false);

  var [error, setError] = useState(null);

  var loadStats = useCallback(function () {
    axios.get(API + '/admin/stats', { withCredentials: true })
      .then(function (r) { setStats(r.data); setError(null); })
      .catch(function (err) {
        if (err.response?.status === 401 || err.response?.status === 403) return;
        setError('Failed to load admin stats: ' + (err.response?.data?.detail || err.message));
      });
  }, []);

  var loadAuditLogs = useCallback(function () {
    axios.get(API + '/admin/audit-logs?per_page=10', { withCredentials: true })
      .then(function (r) { setAuditLogs(r.data.logs || []); })
      .catch(function (err) {
        if (err.response?.status === 401 || err.response?.status === 403) return;
      });
  }, []);

  var loadCoursePurchasers = function () {
    setCpLoading(true);
    axios.get(API + '/admin/course-purchases', { withCredentials: true })
      .then(function (r) { setCoursePurchasers(r.data || []); })
      .catch(function (err) {
        if (err.response?.status === 401 || err.response?.status === 403) return;
      })
      .finally(function () { setCpLoading(false); });
  };

  var loadDeletedUsers = function () {
    setDeletedLoading(true);
    axios.get(API + '/admin/users-deleted', { withCredentials: true })
      .then(function (r) { setDeletedUsers(r.data.users || []); })
      .catch(function () {})
      .finally(function () { setDeletedLoading(false); });
  };

  function downloadUserDataAsPdf(u) {
    setDownloadingUid(u._id);
    axios.get(API + '/admin/users/' + u._id + '/export-data', { withCredentials: true })
      .then(function (r) { return downloadUserDataPdf(r.data, u.name || u.email); })
      .catch(function (e) { alert('Could not export: ' + (e.response?.data?.detail || e.message)); })
      .finally(function () { setDownloadingUid(null); });
  }

  function runRetentionSweep() {
    if (!window.confirm('Run the 90-day retention sweep? This will permanently purge any deleted accounts past their retention window. This cannot be undone.')) return;
    setSweeping(true);
    axios.post(API + '/admin/users/retention-sweep', {}, { withCredentials: true })
      .then(function (r) {
        alert('Retention sweep complete. Permanently purged ' + r.data.purged_count + ' account(s).');
        loadDeletedUsers();
      })
      .catch(function (e) { alert('Sweep failed: ' + (e.response?.data?.detail || e.message)); })
      .finally(function () { setSweeping(false); });
  }

  var loadUsers = useCallback(function () {
    var params = new URLSearchParams({ page: page, per_page: 15 });
    if (search) params.set('search', search);
    if (roleFilter) params.set('role_filter', roleFilter);
    if (planFilter) params.set('plan_filter', planFilter);
    axios.get(API + '/admin/users?' + params.toString(), { withCredentials: true })
      .then(function (r) {
        setUsers(r.data.users);
        setTotal(r.data.total);
        setTotalPages(r.data.total_pages);
        setError(null);
      })
      .catch(function (err) {
        if (err.response?.status === 401 || err.response?.status === 403) return;
        setError('Failed to load users: ' + (err.response?.data?.detail || err.message));
      });
  }, [page, search, roleFilter, planFilter]);

  useEffect(function () { loadStats(); loadAuditLogs(); }, [loadStats, loadAuditLogs]);
  useEffect(function () { loadUsers(); }, [loadUsers]);

  function openEdit(u) {
    setEditUser(u);
    setEditForm({
      role: u.role || 'user',
      subscription_plan: u.subscription_plan || 'free',
      trial_active: u.trial_active || false,
      trial_end_date: u.trial_end_date ? u.trial_end_date.slice(0, 10) : '',
      monthly_price: u.monthly_price || ''
    });
    // Load any courses already granted to this user so the modal can show
    // current state and offer a one-click revoke.
    setGrantedCourses([]);
    setGrantSeries('');
    axios.get(API + '/admin/users/' + u._id + '/granted-courses', { withCredentials: true })
      .then(function (r) { setGrantedCourses(r.data || []); })
      .catch(function () { setGrantedCourses([]); });
  }

  function grantCourse() {
    if (!editUser || !grantSeries) return;
    setGrantBusy(true);
    axios.post(
      API + '/admin/users/' + editUser._id + '/grant-course',
      { series_id: grantSeries },
      { withCredentials: true }
    ).then(function () {
      return axios.get(API + '/admin/users/' + editUser._id + '/granted-courses', { withCredentials: true });
    }).then(function (r) {
      setGrantedCourses(r.data || []);
      setGrantSeries('');
      loadAuditLogs();
    }).catch(function (err) {
      alert(err.response?.data?.detail || 'Grant failed');
    }).finally(function () { setGrantBusy(false); });
  }

  function revokeCourse(seriesId) {
    if (!editUser) return;
    setGrantBusy(true);
    axios.delete(
      API + '/admin/users/' + editUser._id + '/grant-course/' + seriesId,
      { withCredentials: true }
    ).then(function () {
      return axios.get(API + '/admin/users/' + editUser._id + '/granted-courses', { withCredentials: true });
    }).then(function (r) {
      setGrantedCourses(r.data || []);
      loadAuditLogs();
    }).catch(function (err) {
      alert(err.response?.data?.detail || 'Revoke failed');
    }).finally(function () { setGrantBusy(false); });
  }

  function saveEdit() {
    setSaving(true);
    setStripeWarning('');
    var payload = {
      role: editForm.role,
      subscription_plan: editForm.subscription_plan,
      trial_active: editForm.trial_active,
      trial_end_date: editForm.trial_end_date || null,
      monthly_price: editForm.monthly_price !== '' ? parseFloat(editForm.monthly_price) : null
    };
    axios.patch(API + '/admin/users/' + editUser._id, payload, { withCredentials: true })
      .then(function (r) {
        if (r.data.stripe_warning) {
          setStripeWarning(r.data.stripe_warning);
        } else {
          setEditUser(null);
        }
        loadUsers(); loadStats(); loadAuditLogs();
      })
      .catch(function (err) {
        var msg = err.response?.data?.detail || 'Update failed';
        alert(msg);
      })
      .finally(function () { setSaving(false); });
  }

  function handleDeleteUser() {
    setDeleting(true);
    axios.delete(API + '/admin/users/' + deleteTarget._id, { withCredentials: true })
      .then(function () {
        setDeleteTarget(null);
        loadUsers(); loadStats(); loadAuditLogs();
      })
      .catch(function (err) {
        var msg = err.response?.data?.detail || 'Delete failed';
        alert(msg);
      })
      .finally(function () { setDeleting(false); });
  }

  function handleExport() {
    setExporting(true);
    axios.get(API + '/admin/export', { withCredentials: true, responseType: 'blob' })
      .then(function (r) {
        var blob = new Blob([r.data], { type: 'text/csv' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'parentumcustomerinfo.csv';
        document.body.appendChild(a);
        a.click();
        setTimeout(function() {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      })
      .catch(function (err) { console.error('CSV export error:', err); alert('Export failed. Please try again.'); })
      .finally(function () { setExporting(false); });
  }

  var planColors = { free: 'text-white/50', basic: 'text-blue-400', standard: 'text-purple-400', pro: 'text-orange-400' };
  var roleColors = { user: 'bg-white/10 text-white/60', admin: 'bg-red-500/20 text-red-400', test: 'bg-yellow-500/20 text-yellow-400' };

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {error && (
        <div className="flex items-center gap-3 p-4 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl" data-testid="admin-error-banner">
          <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0" />
          <p className="text-sm text-[#EF4444] flex-1">{error}</p>
          <button onClick={function () { setError(null); }} className="text-white/40 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2"><Shield className="w-5 sm:w-6 h-5 sm:h-6 text-red-400" /> Admin Panel</h2>
          <p className="text-xs sm:text-sm text-white/50 mt-1">Manage users, subscriptions, and system settings</p>
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="px-4 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 text-sm w-fit"
          data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <StatCard label="Total Users" value={stats.total_users} icon={Users} color="bg-[#5A4E9E]" />
          <StatCard label="Pro Users" value={stats.by_plan.pro} icon={Crown} color="bg-orange-500" />
          <StatCard label="Trial Active" value={stats.trial_active} icon={Clock} color="bg-cyan-500" />
          <StatCard label="Admins" value={stats.by_role.admin} icon={Shield} color="bg-red-500" />
        </div>
      )}

      {stats && (
        <div className="card-surface p-3 sm:p-4">
          <h3 className="text-xs sm:text-sm font-semibold text-white/50 mb-3">SUBSCRIPTION DISTRIBUTION</h3>
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {['free', 'basic', 'standard', 'pro'].map(function (p) {
              var ct = stats.by_plan[p] || 0;
              var pct = stats.total_users > 0 ? Math.round((ct / stats.total_users) * 100) : 0;
              return (
                <div key={p} className="text-center">
                  <p className={'text-lg font-bold ' + (planColors[p] || '')}>{ct}</p>
                  <p className="text-xs text-white/40 capitalize">{p}</p>
                  <div className="w-full bg-white/5 rounded-full h-1.5 mt-1">
                    <div className="bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] h-1.5 rounded-full transition-all" style={{ width: pct + '%' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activity Feed */}
      {auditLogs.length > 0 && (
        <div className="card-surface p-4" data-testid="audit-log-feed">
          <h3 className="text-sm font-semibold text-white/50 mb-3 uppercase tracking-wider">Recent Admin Activity</h3>
          <div className="space-y-2">
            {auditLogs.slice(0, 8).map(function (log, idx) {
              var ts = log.timestamp ? new Date(log.timestamp).toLocaleString() : '';
              return (
                <div key={log._id || idx} className="flex items-center gap-3 px-3 py-2 bg-white/[0.02] border border-white/5 rounded-lg text-sm">
                  <div className="w-6 h-6 rounded-full bg-[#5A4E9E]/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-3 h-3 text-[#5A4E9E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-white/70 font-medium">{log.action_type}</span>
                    {log.details && <span className="text-white/40 ml-2">— {log.details}</span>}
                  </div>
                  <span className="text-xs text-white/30 flex-shrink-0">{ts}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card-surface p-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input value={search} onChange={function (e) { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name or email..."
              className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-10 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#5A4E9E]"
              data-testid="admin-search-input" />
          </div>
          <div className="flex gap-2">
            <select value={roleFilter} onChange={function (e) { setRoleFilter(e.target.value); setPage(1); }}
              className="bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-[#5A4E9E] flex-1 sm:flex-none"
              data-testid="admin-role-filter">
              <option value="">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="test">Test</option>
            </select>
            <select value={planFilter} onChange={function (e) { setPlanFilter(e.target.value); setPage(1); }}
              className="bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-[#5A4E9E] flex-1 sm:flex-none"
              data-testid="admin-plan-filter">
              <option value="">All Plans</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="pro">Pro</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-xs sm:text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-white/40 border-b border-white/10">
                <th className="pb-3 pr-3 sm:pr-4">Name</th>
                <th className="pb-3 pr-3 sm:pr-4">Email</th>
                <th className="pb-3 pr-3 sm:pr-4">Role</th>
                <th className="pb-3 pr-3 sm:pr-4">Plan</th>
                <th className="pb-3 pr-3 sm:pr-4">Trial</th>
                <th className="pb-3 pr-3 sm:pr-4">Price</th>
                <th className="pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(function (u) {
                return (
                  <tr key={u._id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={'user-row-' + u._id}>
                    <td className="py-2.5 sm:py-3 pr-3 sm:pr-4">
                      <div className="font-medium truncate max-w-[80px] sm:max-w-none">{u.name || '—'}</div>
                    </td>
                    <td className="py-2.5 sm:py-3 pr-3 sm:pr-4 text-white/60">{u.email}</td>
                    <td className="py-2.5 sm:py-3 pr-3 sm:pr-4">
                      <span className={'px-1.5 sm:px-2 py-0.5 rounded text-xs sm:text-xs font-medium ' + (roleColors[u.role] || roleColors.user)}>
                        {u.role || 'user'}
                      </span>
                    </td>
                    <td className={'py-2.5 sm:py-3 pr-3 sm:pr-4 capitalize font-medium text-xs sm:text-sm ' + (planColors[u.subscription_plan] || '')}>{u.subscription_plan || 'free'}</td>
                    <td className="py-2.5 sm:py-3 pr-3 sm:pr-4">
                      {u.trial_active ? (
                        <span className="text-cyan-400 text-xs">{u.days_remaining}d left</span>
                      ) : (
                        <span className="text-white/20 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-2.5 sm:py-3 pr-3 sm:pr-4 text-white/50">{u.monthly_price ? '$' + u.monthly_price : '—'}</td>
                    <td className="py-3">
                      <div className="flex gap-1">
                        <button onClick={function () { downloadUserDataAsPdf(u); }}
                          disabled={downloadingUid === u._id}
                          className="p-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-30"
                          data-testid={'download-user-data-' + u._id}
                          title="Download all of this user's data as a branded PDF">
                          <FileText className="w-3.5 h-3.5 text-white/50" />
                        </button>
                        <button onClick={function () { openEdit(u); }}
                          className="p-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                          data-testid={'edit-user-' + u._id}>
                          <Edit3 className="w-3.5 h-3.5 text-white/50" />
                        </button>
                        <button onClick={function () { setDeleteTarget(u); }}
                          className="p-1.5 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                          data-testid={'delete-user-' + u._id}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 text-sm text-white/50">
          <span>Showing {users.length} of {total} users</span>
          <div className="flex items-center gap-2">
            <button onClick={function () { setPage(Math.max(1, page - 1)); }} disabled={page <= 1}
              className="p-1.5 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30" data-testid="admin-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3">{page} / {totalPages}</span>
            <button onClick={function () { setPage(Math.min(totalPages, page + 1)); }} disabled={page >= totalPages}
              className="p-1.5 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30" data-testid="admin-next-page">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ====== Deleted Accounts (90-day retention) ====== */}
      <div className="card-surface p-3 sm:p-6 overflow-x-auto" data-testid="admin-deleted-accounts-panel">
        <button onClick={function () { var n = !deletedOpen; setDeletedOpen(n); if (n) loadDeletedUsers(); }}
          className="w-full flex items-center justify-between gap-3 mb-2"
          data-testid="admin-deleted-accounts-toggle">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-r from-[#EF4444] to-[#2D2560] flex items-center justify-center flex-shrink-0"><UserX className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div>
            <h3 className="text-base sm:text-lg font-bold">Deleted Accounts (90-day Retention)</h3>
            {deletedUsers.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#EF4444]/15 text-[#EF4444]">
                {deletedUsers.length}
              </span>
            )}
          </div>
          <ChevronDown className={'w-4 h-4 text-white/40 transition-transform ' + (deletedOpen ? 'rotate-180' : '')} />
        </button>

        {deletedOpen && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-white/40">
                Soft-deleted user accounts. Data is preserved for download for {90} days from the deletion date, then permanently purged. The hourly retention loop also runs this sweep automatically.
              </p>
              <button onClick={runRetentionSweep} disabled={sweeping}
                className="px-3 py-1.5 bg-[#EF4444]/15 border border-[#EF4444]/30 text-[#EF4444] text-xs font-medium rounded-lg hover:bg-[#EF4444]/25 transition-colors disabled:opacity-30"
                data-testid="admin-run-retention-sweep">
                {sweeping ? 'Sweeping…' : 'Run Retention Sweep'}
              </button>
            </div>

            {deletedLoading ? (
              <p className="text-sm text-white/40">Loading…</p>
            ) : deletedUsers.length === 0 ? (
              <p className="text-sm text-white/40">No accounts in the retention window.</p>
            ) : (
              <table className="w-full min-w-[600px] text-xs sm:text-sm" data-testid="admin-deleted-users-table">
                <thead>
                  <tr className="text-left text-white/50 border-b border-white/10">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Deleted At</th>
                    <th className="py-2 pr-3">Days Remaining</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedUsers.map(function (u) {
                    var critical = u.days_remaining <= 7;
                    return (
                      <tr key={u._id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={'deleted-user-row-' + u._id}>
                        <td className="py-2 pr-3 truncate max-w-[140px]">{u.name || '—'}</td>
                        <td className="py-2 pr-3 text-white/60 truncate max-w-[200px]">{u.email}</td>
                        <td className="py-2 pr-3 text-white/50">{u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : '—'}</td>
                        <td className="py-2 pr-3">
                          <span className={'px-1.5 py-0.5 rounded text-xs font-medium ' + (critical ? 'bg-[#EF4444]/15 text-[#EF4444]' : 'bg-white/5 text-white/60')}>
                            {u.days_remaining}d
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <button onClick={function () { downloadUserDataAsPdf(u); }}
                            disabled={downloadingUid === u._id}
                            className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-xs flex items-center gap-1 disabled:opacity-30"
                            data-testid={'download-deleted-user-data-' + u._id}>
                            <FileText className="w-3 h-3" />
                            {downloadingUid === u._id ? 'Building…' : 'Download PDF'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {editUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-user-modal">
          <div className="card-surface p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Edit User</h3>
              <button onClick={function () { setEditUser(null); }} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-white/50">{editUser.name} — {editUser.email}</p>

            <div>
              <label className="text-xs text-white/50 mb-1 block">Role</label>
              <select value={editForm.role} onChange={function (e) { setEditForm(Object.assign({}, editForm, { role: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-[#5A4E9E]"
                data-testid="edit-role-select">
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="test">Test</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-white/50 mb-1 block">Subscription Plan</label>
              <select value={editForm.subscription_plan} onChange={function (e) { setEditForm(Object.assign({}, editForm, { subscription_plan: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-[#5A4E9E]"
                data-testid="edit-plan-select">
                <option value="free">Free</option>
                <option value="basic">Basic ($9.99/mo)</option>
                <option value="standard">Standard ($24.99/mo)</option>
                <option value="pro">Pro ($34.99/mo)</option>
              </select>
              {editForm.subscription_plan !== (editUser.subscription_plan || 'free') && (
                <p className="text-xs text-cyan-400 mt-1">Stripe billing will be updated with proration for mid-cycle changes.</p>
              )}
            </div>

            {stripeWarning && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-xs text-yellow-400 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> {stripeWarning}</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="text-xs text-white/50">Trial Active</label>
              <button onClick={function () { setEditForm(Object.assign({}, editForm, { trial_active: !editForm.trial_active })); }}
                className={'w-10 h-5 rounded-full transition-colors ' + (editForm.trial_active ? 'bg-cyan-500' : 'bg-white/10')}
                data-testid="edit-trial-toggle">
                <div className={'w-4 h-4 bg-white rounded-full transform transition-transform ' + (editForm.trial_active ? 'translate-x-5' : 'translate-x-0.5')} />
              </button>
            </div>

            {editForm.trial_active && (
              <div>
                <label className="text-xs text-white/50 mb-1 block">Trial End Date</label>
                <input type="date" value={editForm.trial_end_date}
                  onChange={function (e) { setEditForm(Object.assign({}, editForm, { trial_end_date: e.target.value })); }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-[#5A4E9E]"
                  data-testid="edit-trial-date" />
              </div>
            )}

            <div>
              <label className="text-xs text-white/50 mb-1 block">Monthly Price Override ($)</label>
              <input type="number" step="0.01" value={editForm.monthly_price}
                onChange={function (e) { setEditForm(Object.assign({}, editForm, { monthly_price: e.target.value })); }}
                placeholder="Leave empty for default"
                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#5A4E9E]"
                data-testid="edit-price-input" />
            </div>

            {/* === Grant Courses (free access) === */}
            <div className="border-t border-white/10 pt-4" data-testid="edit-grant-courses">
              <label className="text-xs text-white/50 mb-2 block">Grant Mastery Series (free)</label>
              <div className="flex gap-2">
                <select
                  value={grantSeries}
                  onChange={function (e) { setGrantSeries(e.target.value); }}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-[#5A4E9E]"
                  data-testid="grant-series-select"
                >
                  <option value="">Select a series…</option>
                  <option value="best-interests">Best Interests Mastery</option>
                  <option value="co-parenting">Co-Parenting Mastery</option>
                  <option value="high-conflict">High-Conflict Strategy Mastery</option>
                  <option value="all-bundle">All-3 Mastery Bundle</option>
                </select>
                <button
                  type="button"
                  onClick={grantCourse}
                  disabled={!grantSeries || grantBusy}
                  className="px-4 py-2 rounded-lg bg-[#2D2560] hover:bg-[#1A1738] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                  data-testid="grant-course-btn"
                >
                  Grant
                </button>
              </div>
              {grantedCourses.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="granted-courses-list">
                  {grantedCourses.map(function (gc) {
                    var labelMap = {
                      'best-interests': 'Best Interests Mastery',
                      'co-parenting': 'Co-Parenting Mastery',
                      'high-conflict': 'High-Conflict Strategy Mastery',
                      'all-bundle': 'All-3 Mastery Bundle',
                    };
                    var label = labelMap[gc.series_id] || gc.series_id;
                    return (
                      <div key={gc.series_id} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                        <span className="text-xs text-emerald-300 flex-1 truncate">{label}</span>
                        <button
                          type="button"
                          onClick={function () { revokeCourse(gc.series_id); }}
                          disabled={grantBusy}
                          className="text-xs text-red-300 hover:text-red-200 disabled:opacity-50"
                          data-testid={'revoke-course-' + gc.series_id}
                        >
                          Revoke
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={function () { setEditUser(null); setStripeWarning(''); }}
                className="flex-1 py-2 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:bg-white/10 transition-colors text-sm">
                Cancel
              </button>
              <button onClick={saveEdit} disabled={saving}
                className="flex-1 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg hover:opacity-90 transition-opacity text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="save-user-btn">
                <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course Purchasers */}
      <div className="card-surface overflow-hidden" data-testid="admin-course-purchases">
        <button onClick={function () { if (!cpOpen) loadCoursePurchasers(); setCpOpen(!cpOpen); }}
          className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
          data-testid="course-purchases-toggle">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-r from-[#A855F7] to-[#2D2560] flex items-center justify-center flex-shrink-0"><GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div>
            <span className="text-sm font-semibold">Course Purchasers</span>
            {coursePurchasers.length > 0 && <span className="text-xs text-white/40">({coursePurchasers.length})</span>}
          </div>
          <ChevronDown className={'w-4 h-4 text-white/40 transition-transform ' + (cpOpen ? 'rotate-180' : '')} />
        </button>

        {cpOpen && (
          <div className="border-t border-white/10 p-4">
            {cpLoading ? (
              <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin"></div></div>
            ) : coursePurchasers.length === 0 ? (
              <p className="text-sm text-white/40 text-center py-4">No course purchases yet.</p>
            ) : (
              <div className="space-y-2">
                {coursePurchasers.map(function (cp) {
                  var isExpanded = cpExpanded === cp.user_id;
                  return (
                    <div key={cp.user_id} className="border border-white/10 rounded-lg overflow-hidden" data-testid={'purchaser-' + cp.user_id}>
                      <button onClick={function () { setCpExpanded(isExpanded ? null : cp.user_id); }}
                        className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors text-left">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-[#A855F7]/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-[#A855F7]">{(cp.name || cp.email || '?')[0].toUpperCase()}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{cp.name || cp.email}</p>
                            <p className="text-xs text-white/40">{cp.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-sm font-bold text-[#10B981]">${cp.total_spent}</p>
                            <p className="text-xs text-white/40">{cp.purchase_count} purchase{cp.purchase_count !== 1 ? 's' : ''}</p>
                          </div>
                          <ChevronDown className={'w-4 h-4 text-white/30 transition-transform ' + (isExpanded ? 'rotate-180' : '')} />
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-white/10 p-3 space-y-2 bg-white/[0.02]">
                          {cp.purchases.length > 0 && (
                            <div>
                              <p className="text-xs text-white/30 uppercase tracking-wider mb-1.5 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Individual Tiers</p>
                              {cp.purchases.map(function (p, i) {
                                var tierColors = { beginner: '#10B981', intermediate: '#3B82F6', expert: '#F97316', master: '#A855F7' };
                                return (
                                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded bg-white/[0.03]">
                                    <div className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tierColors[p.tier] || '#5A4E9E' }}></span>
                                      <span className="text-xs text-white/70">{p.course_id}</span>
                                      <span className="text-xs capitalize px-1.5 py-0.5 rounded bg-white/5 text-white/40">{p.tier}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs">
                                      <span className="text-[#10B981] font-medium">${p.amount}</span>
                                      <span className="text-white/30">{p.purchased_at ? p.purchased_at.split('T')[0] : ''}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {cp.bundles.length > 0 && (
                            <div>
                              <p className="text-xs text-white/30 uppercase tracking-wider mb-1.5 flex items-center gap-1"><Package className="w-3 h-3" /> Bundle Purchases</p>
                              {cp.bundles.map(function (b, i) {
                                return (
                                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded bg-purple-500/5 border border-purple-500/10">
                                    <div className="flex items-center gap-2">
                                      <Package className="w-3.5 h-3.5 text-[#A855F7]" />
                                      <span className="text-xs text-white/70">{b.series_id} — Full Bundle</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs">
                                      <span className="text-[#10B981] font-medium">${b.amount}</span>
                                      <span className="text-white/30">{b.purchased_at ? b.purchased_at.split('T')[0] : ''}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ToS Acceptance Logs */}
      <TosAcceptanceLogs />

      {/* Legal Requests & Data Retention */}
      <LegalRequestsPanel />

      {/* Safety Moderation */}
      <SafetyModerationPanel />

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-user-modal">
          <div className="card-surface p-6 w-full max-w-md space-y-4">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-lg font-bold">Delete User Account</h3>
            </div>
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 space-y-2">
              <p className="text-sm text-white/70"><strong>{deleteTarget.name || 'Unnamed'}</strong> — {deleteTarget.email}</p>
              <p className="text-xs text-red-400">This will permanently delete this user and ALL their data including:</p>
              <ul className="text-xs text-white/50 list-disc list-inside space-y-0.5">
                <li>Journal entries and calendar events</li>
                <li>Plan drafts, scans, and analyses</li>
                <li>Messages, companion data, and course progress</li>
                <li>Stripe subscription (will be cancelled)</li>
              </ul>
              <p className="text-xs text-red-400 font-semibold pt-1">This action is irreversible.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={function () { setDeleteTarget(null); }}
                className="flex-1 py-2 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:bg-white/10 transition-colors text-sm">
                Cancel
              </button>
              <button onClick={handleDeleteUser} disabled={deleting}
                className="flex-1 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="confirm-delete-user-btn">
                <Trash2 className="w-4 h-4" /> {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
